/**
 * Minimal DOM builder.
 *
 * Everything user-supplied goes in via textContent, never innerHTML. The
 * markup this replaces concatenated HTML strings and reached for
 * frappe.utils.escape_html inconsistently — a template name or attribute
 * value with a quote in it was an injection waiting to happen. Building
 * nodes makes escaping structural rather than remembered.
 */

/**
 * @param {string} tag
 * @param {object} [props]  { class, text, html, href, ...attrs, dataset:{} }
 * @param {Array}  [children]
 */
export function el(tag, props, children) {
	const node = document.createElement(tag);
	const p = props || {};

	for (const key of Object.keys(p)) {
		const value = p[key];
		if (value == null || value === false) continue;

		if (key === 'class') {
			node.className = Array.isArray(value) ? value.filter(Boolean).join(' ') : value;
		} else if (key === 'text') {
			node.textContent = value;
		} else if (key === 'html') {
			// Only ever for markup this module authored itself (inline SVG icons).
			node.innerHTML = value;
		} else if (key === 'dataset') {
			for (const dk of Object.keys(value)) {
				if (value[dk] != null) node.dataset[dk] = value[dk];
			}
		} else if (key === 'style') {
			node.setAttribute('style', value);
		} else {
			node.setAttribute(key, value === true ? '' : value);
		}
	}

	appendAll(node, children);
	return node;
}

export function appendAll(node, children) {
	if (!children) return node;
	// Flattened, so a render helper may return either one node or a list of
	// them without its caller having to know which. Passing an un-flattened
	// array straight to appendChild throws in a browser and silently corrupts
	// the tree in a stub, so this is correctness rather than convenience.
	const list = (Array.isArray(children) ? children : [children]).flat(Infinity);
	for (const child of list) {
		if (child == null || child === false) continue;
		node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
	}
	return node;
}

/** Inline SVG from a path spec — icons only, never user data. */
export function svg(paths, opts) {
	const o = opts || {};
	const node = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	node.setAttribute('viewBox', o.viewBox || '0 0 24 24');
	node.setAttribute('aria-hidden', 'true');
	if (o.class) node.setAttribute('class', o.class);
	node.innerHTML = paths;
	return node;
}
